import React from 'react'

import cryptoRandomString from 'crypto-random-string'
import axios from 'axios'
import Dropzone from 'react-dropzone'
import { Line } from 'rc-progress'

// import FileResizer from 'react-image-file-resizer'

// import api from '../../lib/api'

// Notes:
// https://medium.com/@kevinwu/client-side-file-upload-to-s3-using-axios-c9363ec7b530
// https://medium.com/@ashan.fernando/upload-files-to-aws-s3-using-signed-urls-fa0a0cf489db
// https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/S3.html

class Uploader extends React.Component {

  state = {
    currentFile: { file: null, progress: 0 },
    isUploading: false,
    files: null,
    filesComplete: [],
    slug: cryptoRandomString({ length: 16 })
  }

  async _fetchSignedUrl(params) {
    const { fetchSignedUrl } = this.props
    try {
      const results = await fetchSignedUrl(params)
      if (results) {
        return results
      } else {
        throw new Error('Error when fetching signed URL!')
      }
    } catch(e) {
      console.error(e)
    }
  }

  _buildStorageKey(file) {
    const { subFolder } = this.props

    let filename = null
    if (!this.props.filename) {
      const cb = cryptoRandomString({ length: 8 })
      filename = file.name.replace(/ /g, '_').toLowerCase()
      const filenameParts = filename.split('.')
      filename = `${filenameParts[0]}-${cb}.${filenameParts[1]}`
    } else {
      filename = this.props.filename
    }

    if (subFolder) {
      return `${subFolder}/${filename}`
    }
    return filename
  }

  _buildFormData(file, data, storageKey) {

    if (this.props.onError) {
      this.props.onError('')
    }

    const params = {
      'key': storageKey,
      'policy': data['policy'],
      'Content-Type': file.type,
      'x-amz-credential': data['xAmzCredential'],
      'x-amz-algorithm': data['xAmzAlgorithm'],
      'x-amz-date': data['xAmzDate'],
      'x-amz-signature': data['xAmzSignature']
    }

    if (data.metadata) {
      Object.keys(data.metadata).forEach(k => params[k.toLowerCase()] = data.metadata[k])
    } else if (data['xAmzMetaUuid']) {
      params['x-amz-meta-uuid'] = data['xAmzMetaUuid']
    }

    params.file = file
    params.acl = data['acl']

    const formData = new FormData()
    for (let key in params) {
      formData.append(key, params[key])
    }

    return { params, formData }
  }

  _onUploadProgress = (progressEvent, file) => {
    let percentCompleted = Math.round( (progressEvent.loaded * 100) / progressEvent.total )
    this.setState({ currentFile: { file: file.name, progress: percentCompleted } })
    if (this.props.onUploadProgress) {
      this.props.onUploadProgress(percentCompleted, file)
    }
  }

  _onDrop = async (files) => {
    if (this.props.onLoading) this.props.onLoading(true)
    this.setState({ isUploading: true, files, filesComplete: [] })
    
    let file
    const final = []
    for(file of files) {
      const storageKey = this._buildStorageKey(file)
      const data = await this._fetchSignedUrl({ id: this.props.id, key: storageKey })
      const { params, formData } = this._buildFormData(file, data, storageKey)
      
      if (formData) {
        const options = {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: progressEvent => this._onUploadProgress(progressEvent, file) // eslint-disable-line
        }
        const response = await this._doUpload(file, data, formData, options, params)
        if (response) {
          final.push(response)
        }
      }
    }

    this.setState({ isUploading: false })
    if (this.props.onLoading) this.props.onLoading(false)
    
    if (this.props.onUpload) {
      const id = (this.props.id) ? this.props.id : null
      this.props.onUpload(final, id)
    } else {
      console.warn('onUpload is invalid - Final Files: ', final)
    }
  }

  async _doUpload(file, data, formData, options, params) {
    const { filesComplete } = this.state
    
    const s3response = await axios.post(data.s3Url, formData, options)
    if (s3response) {
      filesComplete.push(file.name)
      this.setState({ filesComplete })
      params.url = `${data.s3Url}/${params.key}`
      return { params, s3response, signedUrlData: data, id: this.props.id }
    } else {
      throw new Error('Could not upload.')
    }
  }

  _throwError(message) {
    if (this.props.onError) {
      this.props.onError(message)
    } else {
      throw new Error(message)
    }
  }

  _getContent() {
    const { isUploading, files, currentFile, filesComplete } = this.state
    if (isUploading) {
      const fileRows = files.map((file, key) => {
        let progressUploaded = 0
        if (filesComplete.indexOf(file.name) >= 0) {
          progressUploaded = 100
        } else {
          if (currentFile.file && file.name === currentFile.file) progressUploaded = currentFile.progress
        }
        return (
          <div key={key} className="uploader__progress__row">
            <div className="uploader__progress__row--info">{file.name} {progressUploaded}%</div>
            <div className="uploader__progress__row--progress">
              <Line percent={ progressUploaded } strokeWidth="4" strokeColor="#4037FF" />
            </div>
          </div>
        )
      })
      return (<div className="uploader__progress">
        {fileRows}
      </div>)
    }

    const content = (this.props.content) ? this.props.content : <p>To upload, drag and drop some files here, or click to select files</p>
    const type = (this.props.type) ? this.props.type : null
    const errorOverlay = (this.props.errorOverlay) ? this.props.errorOverlay : "File type must be PNG, GIF or JPG file."
    // const fileUpload = !content && <DropZone.FileUpload />
    const multiple = (this.props.isMultiple) ? this.props.isMultiple : false

    return (
      <Dropzone 
        errorOverlayText={errorOverlay}
        accept={type}
        timeout={30000}
        allowMultiple={multiple}
        onDrop={this._onDrop}
      >
        {({getRootProps, getInputProps}) => (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            {content}
          </div>
        )}
      </Dropzone>
    )
  }

  render () {
    const styles = (this.props.styles) ? this.props.styles : { width: '100%', height: 166 }
    let classes = `uploader`
    if (this.props.className) classes = `${classes} ${this.props.className}`
    return (
      <div className={classes}>
        <div className="uploader__container" style={styles}>
          {this._getContent()}
        </div>
      </div>
    )
  }
}

export default Uploader
